import { useEffect, useState } from "react"
import { Share, X } from "lucide-react"
import {
  isIosDevice,
  markHomeScreenPromptPending,
  shouldShowHomeScreenPrompt,
  writeHomeScreenPromptState,
} from "~/utils/homeScreenPrompt"

type BeforeInstallPromptEvent = Event & {
  prompt: () => Promise<void>
  userChoice: Promise<{ outcome: "accepted" | "dismissed" }>
}

type HomeScreenInstallBannerProps = {
  /** When true, mark the prompt pending (after first email verification). */
  enableFromEmailVerification?: boolean
}

export function HomeScreenInstallBanner({
  enableFromEmailVerification = false,
}: HomeScreenInstallBannerProps) {
  const [visible, setVisible] = useState(false)
  const [deferredPrompt, setDeferredPrompt] = useState<BeforeInstallPromptEvent | null>(null)
  const [showIosHelp, setShowIosHelp] = useState(false)

  useEffect(() => {
    if (enableFromEmailVerification) {
      markHomeScreenPromptPending()
    }
    setVisible(shouldShowHomeScreenPrompt())
  }, [enableFromEmailVerification])

  useEffect(() => {
    function onBeforeInstall(event: Event) {
      event.preventDefault()
      setDeferredPrompt(event as BeforeInstallPromptEvent)
    }

    function onInstalled() {
      writeHomeScreenPromptState("installed")
      setVisible(false)
    }

    window.addEventListener("beforeinstallprompt", onBeforeInstall)
    window.addEventListener("appinstalled", onInstalled)
    return () => {
      window.removeEventListener("beforeinstallprompt", onBeforeInstall)
      window.removeEventListener("appinstalled", onInstalled)
    }
  }, [])

  if (!visible) return null

  async function handleInstall() {
    if (deferredPrompt) {
      await deferredPrompt.prompt()
      const choice = await deferredPrompt.userChoice
      setDeferredPrompt(null)
      if (choice.outcome === "accepted") {
        writeHomeScreenPromptState("installed")
        setVisible(false)
      }
      return
    }

    if (isIosDevice()) {
      setShowIosHelp(true)
      return
    }

    setShowIosHelp(true)
  }

  function handleDismiss() {
    writeHomeScreenPromptState("dismissed")
    setVisible(false)
  }

  return (
    <div
      data-testid="home-screen-install-banner"
      className="mb-4 rounded-xl border border-[var(--color-divider)] bg-[var(--color-surface-card)]/90 p-4 shadow-sm"
      role="region"
      aria-label="Install Doggy Dynamo"
    >
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <p className="text-sm font-semibold text-[var(--color-text-primary)]">
            Add Doggy Dynamo to your Home Screen
          </p>
          <p className="mt-1 text-sm text-[var(--color-text-secondary)]">
            Open it like an app — faster access to Ask Dynamo and your dog&apos;s guidance.
          </p>
          {showIosHelp && (
            <p className="mt-2 text-sm text-[var(--color-text-secondary)]">
              On iPhone or iPad: tap <Share className="inline h-3.5 w-3.5" aria-hidden /> Share, then
              &quot;Add to Home Screen&quot;.
            </p>
          )}
        </div>
        <button
          type="button"
          onClick={handleDismiss}
          className="rounded-full p-1 text-[var(--color-text-secondary)] hover:bg-[var(--color-bg-primary)]"
          aria-label="Dismiss install banner"
        >
          <X size={16} />
        </button>
      </div>
      <div className="mt-3 flex flex-wrap gap-2">
        <button
          type="button"
          onClick={() => void handleInstall()}
          className="rounded-lg bg-[var(--color-text-primary)] px-4 py-2 text-sm font-medium text-[var(--color-surface-card)]"
        >
          {deferredPrompt ? "Install" : "Show me how"}
        </button>
        <button
          type="button"
          onClick={handleDismiss}
          className="rounded-lg border border-[var(--color-divider)] px-4 py-2 text-sm font-medium text-[var(--color-text-primary)]"
        >
          Not now
        </button>
      </div>
    </div>
  )
}
